import type { TutorAIDocumentStatus } from '@/shared/api/generated/types/TutorAIDocumentStatus';

/**
 * Os três status de documento da API, nomeados.
 *
 * O gerado é `1 | 2 | 3`, sem nomes — este mapa existe para o código não espalhar números
 * mágicos. Não é um enum porque o tipo é do spec e não deve ser redeclarado.
 */
export const DOCUMENT_STATUS = {
  pending: 1,
  imported: 2,
  outOfSync: 3,
} as const satisfies Record<string, TutorAIDocumentStatus>;

/** Chave de i18n de um status de documento. */
export type DocumentStatusKey = keyof typeof DOCUMENT_STATUS;

/**
 * Traduz o status numérico da API na chave de i18n correspondente.
 *
 * Status desconhecido cai em `pending` — é a suposição segura: mostra o documento como não
 * indexado em vez de sugerir que ele já participa das respostas do tutor.
 *
 * @param status - Status devolvido pela API
 * @returns Chave usada em `classDetail.documents.status.*`
 */
export function documentStatusKey(status: TutorAIDocumentStatus): DocumentStatusKey {
  if (status === DOCUMENT_STATUS.imported) return 'imported';
  if (status === DOCUMENT_STATUS.outOfSync) return 'outOfSync';
  return 'pending';
}

/**
 * Se o documento aceita (re)importação.
 *
 * Pendente nunca foi indexado; fora de sincronia tem índice que não corresponde ao
 * arquivo. Nos dois casos importar resolve.
 *
 * @param status - Status devolvido pela API
 */
export function canImport(status: TutorAIDocumentStatus): boolean {
  return status === DOCUMENT_STATUS.pending || status === DOCUMENT_STATUS.outOfSync;
}
